// ─────────────────────────────────────────────────────────────────────────────
// AppGate — SIGNED IN, IN FRONT OF THE WHOLE APP (§ F158 → § F201).
//
// § F158 put ONE wall in front of `<Routes>`: the family password. § F201
// (2026-09-30) replaces the password with an ACCOUNT. Nothing below this
// component mounts until a signed-in player is playing on this device, so no
// page, no fetch and no roster runs for a stranger.
//
// DEEP LINKS SURVIVE. A gated path is sent to `/login?next=<the path>` and the
// sign-in comes back to it. (§ F208 retired invite links: invites wait in the app.)
//
// OPEN WITHOUT AN ACCOUNT:
//   /login, /register, /forgot-password, /reset-password — the way in (§ F206)
//   /terms, /privacy         — linked from sign-up
//   /doodleduel (and /party) — a party ROOM is joined with its code and a name
//                              typed per room, and never needed an account
//                              (design § "Party rooms are different", F3)
//
// A SESSION THAT DIED (signed out elsewhere, account deleted) is noticed here:
// the first render checks it with `/api/account/me`, and a definite 401 forgets
// that account on this device — never a network error.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePlayer, useAccounts } from "../lib/usePlayer";
import { forgetAccount } from "../lib/player";
import { fetchMe, AccountApiError } from "../lib/account";

const OPEN_PATH = /^\/(login|register|forgot-password|reset-password|terms|privacy|doodleduel|party)(\/|$)/;

export function isOpenPath(pathname: string): boolean {
  return OPEN_PATH.test(pathname);
}

/** One check per player per page load. */
const checked = new Set<string>();

export function AppGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { player } = usePlayer();
  const accounts = useAccounts();

  useEffect(() => {
    if (!player || checked.has(player.id)) return;
    checked.add(player.id);
    fetchMe(player).catch((err) => {
      if (err instanceof AccountApiError && err.status === 401) forgetAccount(player.id);
    });
  }, [player]);

  if (isOpenPath(location.pathname) || player) return <>{children}</>;

  const here = `${location.pathname}${location.search}${location.hash}`;
  // Signed-in accounts on this device but nobody chosen: pick one of THEM.
  // `key={location.key}` — a <Navigate> only redirects when it MOUNTS. Signing
  // out on `/` rendered it, then sign-out's own navigate("/") landed on the same
  // still-mounted element: no redirect, a blank page (§ F207). Keying it on the
  // navigation makes every arrival at a gated path redirect.
  if (accounts.length > 0 && location.pathname !== "/who") {
    return <Navigate key={location.key} to={`/who?from=${encodeURIComponent(here)}`} replace />;
  }
  if (location.pathname === "/who" && accounts.length > 0) return <>{children}</>;
  const next = here === "/" ? "" : `?next=${encodeURIComponent(here)}`;
  return <Navigate key={location.key} to={`/login${next}`} replace />;
}

export default AppGate;
